import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CostCategory } from "../types";
import { costService } from "../services/dataService";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./PurchaseOrders.css";
import "./CategoryForm.css";

interface CategoryFormProps {}

const CategoryForm: React.FC<CategoryFormProps> = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "new");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState<CostCategory | null>(null);
  // Kaydet'e basıldıktan sonra alan altı hata metinlerini göster
  const [tried, setTried] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    categoryName: "",
    description: "",
  });

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  const loadCategory = React.useCallback(
    async (categoryId: string) => {
      try {
        setLoading(true);
        const data = await costService.getCostCategoryById(categoryId);
        setCategory(data);
        setFormData({
          categoryName: data.categoryName,
          description: data.description || "",
        });
      } catch (error) {
        console.error("❌ Failed to load category:", error);
        setAlertModal({
          isOpen: true,
          message: "Kategori yüklenemedi!",
          type: "error",
        });
        navigate("/cost");
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (isEditing && id) {
      loadCategory(id);
    }
  }, [id, isEditing, loadCategory]);

  const handleSave = async () => {
    setTried(true);
    if (!formData.categoryName.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Kategori adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      if (isEditing && category) {
        // Update existing category
        const updatedCategory = {
          ...category,
          ...formData,
        };
        await costService.updateCostCategory(
          category.costCategoryId,
          updatedCategory,
        );
        setAlertModal({
          isOpen: true,
          message: "Kategori başarıyla güncellendi!",
          type: "success",
        });
      } else {
        // Create new category
        const newCategory = {
          ...formData,
          category: formData.categoryName, // Backend expects this field
          isActive: true,
        };
        await costService.createCostCategory(newCategory);
        setAlertModal({
          isOpen: true,
          message: "Kategori başarıyla oluşturuldu!",
          type: "success",
        });
      }

      // Navigate'i onClose'da yapılacak
    } catch (error) {
      console.error("❌ Failed to save category:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategori kaydedilemedi!",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!isEditing || !category) return;

    setConfirmModal({
      isOpen: true,
      message:
        "Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!",
      type: "danger",
      onConfirm: async () => {
        try {
          await costService.deleteCostCategory(category.costCategoryId);
          setAlertModal({
            isOpen: true,
            message: "Kategori başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("❌ Failed to delete category:", error);
          setAlertModal({
            isOpen: true,
            message:
              "Kategori silinemedi! Bu kategoriye bağlı maliyet kalemleri olabilir.",
            type: "error",
          });
        }
      },
    });
  };

  const nameErr = tried && !formData.categoryName.trim();

  return (
    <div className="po-page cf-page">
      <header className="po-head">
        <div>
          <button type="button" className="po-link cf-back" onClick={() => navigate("/cost")}>
            ← Maliyet Yönetimi
          </button>
          <span className="po-kicker">Maliyet</span>
          <h1>{isEditing ? "Kategoriyi Düzenle" : "Yeni Kategori"}</h1>
          <p>
            {isEditing
              ? "Kategorinin adını ve açıklamasını güncelleyin."
              : "Maliyet kalemlerini gruplamak için yeni bir kategori oluşturun."}
          </p>
        </div>
        <div className="po-head-actions">
          {isEditing && (
            <button
              type="button"
              className="po-btn danger"
              onClick={handleDeleteCategory}
              disabled={loading || !category}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
              </svg>
              Sil
            </button>
          )}
          <button
            type="button"
            className="po-btn primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </header>

      <section className={`po-card cf-card${saving ? " dim" : ""}`}>
        {loading ? (
          <div className="po-loading">
            <span className="po-spin" /> Yükleniyor...
          </div>
        ) : (
          <div className="cf-body">
            <div className="po-form-sec">
              <h4>Kategori bilgileri</h4>
              <div className="po-grid2">
                <label className="po-field">
                  <span>
                    Kategori adı<em className={nameErr ? "err" : ""}>zorunlu</em>
                  </span>
                  <input
                    type="text"
                    value={formData.categoryName}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryName: e.target.value })
                    }
                    placeholder="Örn: Hammaddeler, İşçilik, Genel Giderler"
                    className={nameErr ? "is-invalid" : undefined}
                    aria-invalid={nameErr || undefined}
                    required
                  />
                  {nameErr && (
                    <span className="po-fielderr">Kategori adı zorunludur.</span>
                  )}
                </label>
              </div>
              <label className="po-field">
                <span>
                  Açıklama<em>isteğe bağlı</em>
                </span>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Kategori hakkında açıklama (opsiyonel)"
                  rows={3}
                />
              </label>
            </div>
          </div>
        )}
      </section>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => {
          setAlertModal({ ...alertModal, isOpen: false });
          if (alertModal.type === "success") {
            navigate("/cost");
          }
        }}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default CategoryForm;
